import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '@/lib/env';
import { verifyGeoComplyToken, getJwtSecret } from '@/lib/geocomplyJwt';
import { storage } from '@/lib/storage';

/**
 * GeoComply Frontend Hook - React Hook for GeoComply Core NDBS SDK Integration
 * 
 * This hook provides a React interface for the GeoComply Core NDBS SDK.
 * It handles SDK initialization, geolocation checks, and communication with the backend.
 * 
 * HOW IT WORKS:
 * 1. Loads GeoComply SDK script dynamically from CDN
 * 2. Initializes GcHtml5 instance with license key
 * 3. Provides checkGeolocation() function to perform geolocation checks
 * 4. Sends encrypted response to backend for verification
 * 5. Returns compliance status and location data
 * 
 * INTEGRATION POINTS:
 * - Registration: Check before user signs up
 * - Login: Check before user logs in
 * - Purchase: Check before processing purchase
 * - Redemption: Check before creating redemption request
 * - Periodic: Automatic checks every 20 minutes (via GeoComplyProvider)
 */

/**
 * GeoComply SDK types - GcHtml5 is added to window object when SDK loads
 * 
 * The SDK can expose GcHtml5 in two ways:
 * 1. Factory object with createClient/createPrecheckClient methods (newer API)
 * 2. Constructor function/class (older API)
 * 
 * We use 'any' here because the SDK API can vary and we do runtime detection
 */
declare global {
  interface Window {
    GcHtml5?: any; // GeoComply NDBS SDK - can be factory object or constructor
  }
}

/**
 * Failure reason codes from GeoComply
 * These indicate why a location check failed
 */
export type GeoComplyFailureReason = 
  | 'boundary' // User is outside allowed geographic boundary
  | 'proxy' // VPN or proxy detected
  | 'vpn' // VPN detected (alias for proxy)
  | 'devtools' // Developer tools or browser extension detected
  | 'location_unavailable' // Location could not be determined
  | 'unknown'; // Unknown failure reason

/**
 * Result structure returned by geolocation checks
 */
export interface GeoComplyCheckResult {
  success: boolean; // Whether the check completed successfully
  isCompliant: boolean; // Whether user's location is in an allowed jurisdiction
  error?: string; // Error message if check failed
  failureReasons?: GeoComplyFailureReason[]; // Specific reasons why compliance failed
  location?: {
    country?: string; // ISO country code (e.g., "US", "CA")
    region?: string; // State/province code (e.g., "CA", "NY")
    city?: string; // City name
  };
}

/**
 * Return type for useGeoComply hook
 */
export interface UseGeoComplyReturn {
  checkGeolocation: () => Promise<GeoComplyCheckResult>; // Function to perform geolocation check
  isChecking: boolean; // Whether a check is currently in progress
  isInitialized: boolean; // Whether the SDK is initialized and ready
  lastCheckResult: GeoComplyCheckResult | null; // Result of the last check
  error: string | null; // Any error that occurred during initialization or checks
}

/**
 * Main React hook for GeoComply Core NDBS SDK integration
 * 
 * This hook manages the GeoComply SDK lifecycle and provides a simple interface
 * for performing geolocation checks. It handles:
 * - SDK script loading
 * - SDK initialization with license key
 * - Geolocation check execution
 * - Backend verification
 * - Error handling and state management
 * 
 * USAGE:
 * ```tsx
 * const { checkGeolocation, isInitialized, isChecking } = useGeoComply();
 * 
 * const handleCheck = async () => {
 *   if (isInitialized) {
 *     const result = await checkGeolocation();
 *     if (result.isCompliant) {
 *       // Proceed with action
 *     }
 *   }
 * };
 * ```
 * 
 * @returns UseGeoComplyReturn object with check function and state
 */
export function useGeoComply(): UseGeoComplyReturn {
  const [isChecking, setIsChecking] = useState(false); // Loading state for checks
  const [isInitialized, setIsInitialized] = useState(false); // SDK initialization status
  const [lastCheckResult, setLastCheckResult] = useState<GeoComplyCheckResult | null>(null); // Last check result
  const [error, setError] = useState<string | null>(null); // Error state
  const gcInstanceRef = useRef<any>(null); // Reference to GeoComply GcHtml5 instance
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null); // Reference to periodic check interval

  // Helper to get user info from localStorage (avoids circular dependency with AuthContext)
  const getUserInfo = useCallback(() => {
    try {
      const storedUser = storage.getItem('user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch (error) {
      // Ignore parsing errors
    }
    return null;
  }, []);

  /**
   * Get GeoComply license from backend with caching
   * 
   * According to GeoComply documentation, the license should be fetched from the backend
   * to ensure it's always valid. This function implements caching to avoid unnecessary
   * network requests.
   * 
   * @returns Promise<string> - The license key string
   */
  const getLicenseFromBackend = async (): Promise<string> => {
    // Check cache first
    const cacheKey = 'geocomply_license_cache';
    const cached = storage.getItem(cacheKey);
    
    // One-time cleanup: Check if old cache format exists and clear it
    // Old cache might have been created before backend license fetching was implemented
    const oldCacheKey = 'geocomply_license_cache_old';
    if (storage.getItem(oldCacheKey)) {
      storage.removeItem(oldCacheKey);
    }
    
    if (cached) {
      try {
        const { license, expires } = JSON.parse(cached);
        // Validate that license exists and is not empty
        if (license && license.trim().length > 0) {
          // Check if license is still valid
          if (new Date(expires) > new Date()) {
            return license;
          } else {
            // Clear expired cache
            storage.removeItem(cacheKey);
          }
        } else {
          // Cached license is empty, clear invalid cache
          storage.removeItem(cacheKey);
        }
      } catch {
        // Invalid cache, fetch new license
        // Clear invalid cache
        storage.removeItem(cacheKey);
      }
    }
    
    // Fetch license from backend
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/geocomply/license`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        // Try to parse error response body for better error message
        let errorMessage = response.statusText || 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If response isn't JSON, use statusText
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }
        throw new Error(`Failed to fetch license: ${errorMessage}`);
      }
      
      const data = await response.json();
      
      // Validate license is present and not empty
      if (!data.license || typeof data.license !== 'string' || data.license.trim().length === 0) {
        throw new Error('Backend returned empty or invalid license');
      }
      
      // Cache the license
      storage.setItem(cacheKey, JSON.stringify({
        license: data.license,
        expires: data.expires,
      }));
      
      return data.license;
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Check if GeoComply should be bypassed
   * Returns true if:
   * 1. Running on localhost (development mode), OR
   * 2. storage.SKIP_GEOCOMPLY is set to 'true' (console override)
   *
   * Console usage: storage.setItem('SKIP_GEOCOMPLY', 'true')
   * To re-enable: storage.removeItem('SKIP_GEOCOMPLY')
   */
  const isLocalhost = (): boolean => {
    if (typeof window === 'undefined') return false;

    // Check for localStorage override flag (persists across page refresh)
    try {
      if (storage.getItem('SKIP_GEOCOMPLY') === 'true') {
        return true;
      }
    } catch {
      // localStorage not available
    }

    const hostname = window.location.hostname;
    const port = window.location.port;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      (hostname === 'localhost' && port === '8080') ||
      window.location.href.includes('localhost:8080')
    );
  };

  /**
   * Initialize GeoComply SDK on component mount
   * 
   * This effect runs once when the hook is first used. It:
   * 1. Loads the GeoComply SDK script from CDN (if not already loaded)
   * 2. Initializes the GcHtml5 instance with the license key
   * 3. Sets up error handling and state management
   * 4. Cleans up on unmount
   * 
   * The SDK script is loaded asynchronously to avoid blocking page load.
   */
  useEffect(() => {
    // Skip GeoComply initialization if running on localhost (development)
    if (isLocalhost()) {
      // Mark as initialized but disabled - this allows the app to work without GeoComply
      setIsInitialized(true);
      setError(null);
      return;
    }

    const initializeGeoComply = async () => {
      try {
        // Step 1: Load SDK script if not already loaded
        // The SDK adds GcHtml5 to the window object when loaded
        if (!window.GcHtml5) {
          try {
            await loadGeoComplySDK();
          } catch (loadError: any) {
            // SDK failed to load - don't crash the app
            setError(`GeoComply SDK unavailable: ${loadError.message || 'SDK URL may be incorrect or network issue'}`);
            setIsInitialized(false);
            return; // Exit early - can't initialize without SDK
          }
        }

        // Step 2: Initialize SDK if available
        if (window.GcHtml5) {
          
          const GcHtml5Type = typeof window.GcHtml5;
          const isObject = typeof window.GcHtml5 === 'object' && window.GcHtml5 !== null;
          const isFunction = typeof window.GcHtml5 === 'function';
          
          // Check if it's the new factory API (object with createClient/createPrecheckClient methods)
          const hasCreateClient = isObject && typeof window.GcHtml5.createClient === 'function';
          const hasCreatePrecheckClient = isObject && typeof window.GcHtml5.createPrecheckClient === 'function';
          
          // Check if it's the old constructor API (function/class)
          const isConstructor = isFunction && (window.GcHtml5.prototype || window.GcHtml5.toString().includes('class'));
          
          if (!isObject && !isFunction) {
            setError(`GeoComply SDK loaded but GcHtml5 is not a valid type (type: ${GcHtml5Type}). Please check the SDK URL and version.`);
            setIsInitialized(false);
            return;
          }

          // Get license key from backend (per GeoComply best practices)
          // The license should be fetched from the backend, not from environment variables
          const licenseKey = await getLicenseFromBackend();
          
          // Validate license key is configured
          if (!licenseKey) {
            setError('GeoComply license key not available from backend');
            setIsInitialized(false);
            return;
          }

          // Step 3: Initialize GeoComply instance
          // Try new factory API first (createClient), then fall back to constructor API
          try {
            if (hasCreateClient) {
              // New factory API: use createClient method
              try {
                // CORRECT API: createClient() takes NO parameters per GeoComply documentation
                const clientInstance = window.GcHtml5.createClient();
                
                if (!clientInstance) {
                  throw new Error('createClient() returned null or undefined');
                }
                
                // Set license using setLicense() method (per documentation)
                clientInstance.setLicense(licenseKey);
                
                // Store the instance
                gcInstanceRef.current = clientInstance;
                
                // Note: Event handlers are registered per-request in checkGeolocation()
                // to handle each request's response individually
                
                // Client is now initialized and ready to use
                setIsInitialized(true);
                setError(null);
                
              } catch (createError: any) {
                setError(`Failed to create GeoComply client: ${createError.message || 'Unknown error'}`);
                setIsInitialized(false);
              }
            } else {
              setError('GeoComply SDK createClient() method not available. Please check SDK version.');
              setIsInitialized(false);
            }
          } catch (initError: any) {
            // Handle initialization errors
            setError(`Failed to create GeoComply instance: ${initError.message || 'Unknown error'}`);
            setIsInitialized(false);
          }
        } else {
          // GcHtml5 not available even after attempting to load
          setError('GeoComply SDK not available');
          setIsInitialized(false);
        }
      } catch (err: any) {
        // Handle any errors during initialization (script load failure, etc.)
        setError(err.message || 'Failed to initialize GeoComply SDK');
        setIsInitialized(false);
      }
    };

    // Start initialization
    initializeGeoComply();

    // Cleanup on component unmount
    return () => {
      // Clear any periodic check intervals
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      // Destroy GeoComply instance if it has a destroy method
      if (gcInstanceRef.current) {
        try {
          gcInstanceRef.current.destroy?.();
        } catch {
          // Ignore errors destroying instance
        }
      }
    };
  }, []);

  /**
   * Dynamically loads the GeoComply NDBS SDK script
   * 
   * This function loads the GeoComply SDK JavaScript file from the CDN.
   * The SDK adds the GcHtml5 class to the window object when loaded.
   * 
   * SDK URL:
   * - Can be configured via VITE_GEOCOMPLY_SDK_URL environment variable
   * - Defaults to official GeoComply CDN: https://sdk.geocomply.com/ndbs/ndbs.min.js
   * 
   * FEATURES:
   * - Prevents duplicate loading (checks if already loaded)
   * - Handles concurrent load attempts (multiple components calling simultaneously)
   * - Async loading (doesn't block page render)
   * - Graceful error handling (doesn't crash app if SDK fails to load)
   * 
   * @returns Promise that resolves when SDK is loaded and GcHtml5 is available
   */
  const loadGeoComplySDK = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if SDK is already loaded (from previous load or another component)
      if (window.GcHtml5) {
        resolve();
        return;
      }

      // Get SDK URL from environment - VITE_GEOCOMPLY_SDK_URL must be set
      // Production: https://cdn.geocomply.com/322/gc-html5.js
      // Staging: https://stg-cdn.geocomply.com/342/gc-html5.js
      const sdkUrl = import.meta.env.VITE_GEOCOMPLY_SDK_URL;

      // Validate SDK URL is configured
      if (!sdkUrl) {
        reject(new Error('GeoComply SDK URL not configured'));
        return;
      }

      // Check if script is already in the DOM (being loaded by another component)
      const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
      if (existingScript) {
        // Wait for existing script to finish loading
        existingScript.addEventListener('load', () => {
          if (window.GcHtml5) {
            resolve();
          } else {
            reject(new Error('GeoComply SDK loaded but GcHtml5 not available'));
          }
        });
        existingScript.addEventListener('error', () => {
          reject(new Error('Failed to load GeoComply SDK'));
        });
        return;
      }

      // Create and inject script tag
      const script = document.createElement('script');
      script.src = sdkUrl;
      script.async = true; // Load asynchronously to avoid blocking
      
      // Handle successful load
      script.onload = () => {
        // Wait a brief moment for SDK to fully initialize (some SDKs need time to set up)
        setTimeout(() => {
          // Verify GcHtml5 is available (SDK should add it to window)
          if (window.GcHtml5) {
            // Log detailed information about GcHtml5 for debugging
            const GcHtml5Type = typeof window.GcHtml5;
            const isFunction = typeof window.GcHtml5 === 'function';
            const isObject = typeof window.GcHtml5 === 'object' && window.GcHtml5 !== null;
            const hasCreateClient = isObject && typeof window.GcHtml5.createClient === 'function';
            const hasCreatePrecheckClient = isObject && typeof window.GcHtml5.createPrecheckClient === 'function';
            
            // Check if it's a valid API (either factory object or constructor)
            if (!isFunction && !isObject) {
              reject(new Error(`GeoComply SDK loaded but GcHtml5 is not a valid type (type: ${GcHtml5Type})`));
              return;
            }
            
            // Validate it has at least one of the expected APIs
            if (isObject && !hasCreateClient && !hasCreatePrecheckClient) {
              // Don't reject - might still work with constructor API
            }
            
            resolve();
          } else {
            reject(new Error('GeoComply SDK loaded but GcHtml5 not available'));
          }
        }, 100); // Small delay to allow SDK to fully initialize
      };
      
      // Handle load errors (network failure, 404, DNS resolution failure, etc.)
      script.onerror = () => {
        reject(new Error(`Failed to load GeoComply SDK from ${sdkUrl}. Please verify the SDK URL is correct.`));
      };
      
      // Append to document head to start loading
      document.head.appendChild(script);
    });
  };

  /**
   * Performs a geolocation check using GeoComply SDK
   * 
   * This is the main function for checking user location. It:
   * 1. Sets required parameters (userId, sessionId, license, reasonCode)
   * 2. Registers event handlers for this specific request
   * 3. Calls GeoComply SDK's request() method
   * 4. Receives encrypted location data from SDK via success event
   * 5. Sends encrypted data to backend for decryption and verification
   * 6. Returns compliance status and location information
   * 
   * FLOW:
   * Frontend SDK → Encrypted Response → Backend Decryption → Compliance Validation → Result
   * 
   * SECURITY:
   * - Location data is encrypted by GeoComply SDK
   * - Decryption only happens on backend (never trust frontend)
   * - Backend validates compliance against jurisdiction rules
   * 
   * INTEGRATION POINTS:
   * This function is called at:
   * - User registration (AuthPage)
   * - User login (AuthPage)
   * - Before purchase (GetCoinsPage)
   * - Before redemption (RedeemModal)
   * - Periodically every 20 minutes (GeoComplyProvider)
   * 
   * DIAGNOSTIC LOGGING:
   * This function includes comprehensive logging to diagnose SDK errors (like error 614):
   * - Initial state: User info, client methods availability
   * - Parameter setting: userId, sessionId, license, reasonCode
   * - Request timing: When request() is called, handler execution times
   * - Handler execution: Which handler fires (success/failed), timing, state
   * - SDK error details: Client state, methods, event listeners when errors occur
   * - Backend communication: Request/response timing and status
   * 
   * These logs help identify:
   * - Missing or invalid parameters
   * - SDK initialization issues
   * - Event handler problems
   * - Timing/race conditions
   * - SDK internal errors
   * 
   * @returns Promise<GeoComplyCheckResult> with compliance status and location
   */
  const checkGeolocation = useCallback(async (): Promise<GeoComplyCheckResult> => {
    // Check if running on localhost (development mode)
    // If so, return compliant result without actually checking
    if (isLocalhost()) {
      return {
        success: true,
        isCompliant: true,
        location: {
          country: 'US',
          region: 'CA',
          city: 'Local Development',
        },
      };
    }

    // Validate SDK is initialized before attempting check
    if (!isInitialized || !gcInstanceRef.current) {
      return {
        success: false,
        isCompliant: false,
        error: 'GeoComply SDK not initialized',
      };
    }

    // Set loading state
    setIsChecking(true);
    setError(null);

    try {
      // CORRECT API: According to GeoComply documentation:
      // 1. Set required parameters before each request
      // 2. Register event handlers for this specific request
      // 3. Call request() method (NOT geolocate())
      
      const userInfo = getUserInfo();
      const client = gcInstanceRef.current;
      
      // Get license key from backend (with caching)
      const licenseKey = await getLicenseFromBackend();
      
      // Set required parameters before making request (per documentation)
      // IMPORTANT: Always use UUID for GeoComply (not ID) to ensure consistent mapping
      let userIdSet: string | null = null;
      if (userInfo?.uuid) {
        // Use UUID as the primary identifier (required for GeoComply data mapping)
        userIdSet = userInfo.uuid;
        client.setUserId(userInfo.uuid);
      } else if (userInfo?.id) {
        // Fallback to ID only if UUID is not available (should not happen in production)
        userIdSet = String(userInfo.id);
        client.setUserId(userIdSet);
      }
      
      // Set session ID (use a session identifier if available)
      let sessionId: string | null = null;
      if (userInfo) {
        sessionId = storage.getItem('session_id') || `session_${Date.now()}`;
        client.setUserSessionId(sessionId);
      }
      
      // Set license (refresh if needed)
      client.setLicense(licenseKey);
      
      // Set reason code - use LOGIN for initial checks, INTERVAL for periodic
      let reasonCode: string | null = null;
      if (client.REASON_CODE) {
        reasonCode = client.REASON_CODE.LOGIN || 'LOGIN';
        client.setReasonCode(reasonCode);
      }

      // Make the request and handle response via event handlers
      const requestStartTime = Date.now();
      const result = await new Promise<GeoComplyCheckResult>((resolve, reject) => {
        let successHandlerCalled = false;
        let failedHandlerCalled = false;
        let requestCompleted = false;
        
        // Set up one-time event handlers for this specific request
        const successHandler = async (encryptedResponse: string) => {
          if (successHandlerCalled) {
            return; // Prevent double processing
          }
          
          successHandlerCalled = true;
          requestCompleted = true;
          
          try {
            // Remove handlers after receiving response
            client.events.off('success', successHandler);
            client.events.off('failed', failedHandler);
            
            // Send encrypted response to backend for verification
            const apiUrl = getApiUrl();
            const authToken = userInfo ? await getAuthToken() : null;
            
            // Include user info in request body if not authenticated (e.g., during registration)
            const requestBody: any = {
              encryptedResponse: encryptedResponse,
            };
            
            // If no auth token but we have user info, include it in the body
            if (!authToken && userInfo) {
              requestBody.userUuid = userInfo.uuid;
              requestBody.userEmail = userInfo.email;
              requestBody.userType = userInfo.type || 'customer';
            }
            
            const response = await fetch(`${apiUrl}/geocomply/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(authToken && {
                  'Authorization': `Bearer ${authToken}`,
                }),
              },
              body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
              throw new Error(`Backend verification failed: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Try to verify JWT token if secret is configured
            // If JWT verification fails, fall back to using backend response directly
            const jwtSecret = getJwtSecret();
            let checkResult: GeoComplyCheckResult;
            
            if (jwtSecret && data.token) {
              try {
                // Attempt JWT verification
                const verifiedPayload = await verifyGeoComplyToken(data.token, jwtSecret);
                
                checkResult = {
                  success: verifiedPayload.success,
                  isCompliant: verifiedPayload.isCompliant,
                  location: verifiedPayload.location,
                  failureReasons: verifiedPayload.failureReasons,
                  error: verifiedPayload.error,
                };
              } catch {
                // JWT verification failed, use backend response directly (backend is trusted source)
                checkResult = {
                  success: data.success ?? true,
                  isCompliant: data.isCompliant ?? false,
                  location: data.location,
                  failureReasons: data.failureReasons,
                  error: data.error,
                };
              }
            } else {
              // No JWT secret configured or no token, use backend response directly (backend is trusted source)
              checkResult = {
                success: data.success ?? true,
                isCompliant: data.isCompliant ?? false,
                location: data.location,
                failureReasons: data.failureReasons,
                error: data.error,
              };
            }
            
            resolve(checkResult);
          } catch (err: any) {
            reject(err);
          }
        };
        
        const failedHandler = (code: number, message: string) => {
          if (failedHandlerCalled) {
            return; // Prevent double processing
          }
          
          failedHandlerCalled = true;
          requestCompleted = true;
          
          // Remove handlers
          try {
            client.events.off('success', successHandler);
            client.events.off('failed', failedHandler);
          } catch {
            // Ignore errors removing handlers
          }
          
          reject(new Error(message || `Geolocation check failed with code ${code}`));
        };
        
        // Register handlers for this request
        client.events.on('success', successHandler);
        client.events.on('failed', failedHandler);
        
        // Make the geolocation request
        try {
          client.request();
        } catch (requestError: any) {
          // Remove handlers if request() throws synchronously
          client.events.off('success', successHandler);
          client.events.off('failed', failedHandler);
          reject(requestError);
        }
      });

      // Store result for reference
      setLastCheckResult(result);
      return result;
    } catch (err: any) {
      // Handle any errors during the check process
      const errorMessage = err.message || 'Failed to check geolocation';
      setError(errorMessage);
      
      // Check if this is a GeoComply SDK error (technical error, not compliance failure)
      // Error codes like 614 are SDK/technical errors, not location compliance issues
      const isSdkError = errorMessage.includes('code 614') || 
                        errorMessage.includes('unexpected error') ||
                        errorMessage.includes('Geolocation check failed with code');
      
      const errorResult: GeoComplyCheckResult = {
        success: false,
        // For SDK errors, set isCompliant to undefined to indicate "unknown" (not non-compliant)
        // This allows the calling code to distinguish between:
        // - SDK errors (isCompliant: undefined) = technical error, might allow to proceed
        // - Non-compliant locations (isCompliant: false) = location not allowed
        // - Other errors (isCompliant: false) = fail securely
        isCompliant: isSdkError ? undefined as any : false,
        error: errorMessage,
      };
      setLastCheckResult(errorResult);
      return errorResult;
    } finally {
      // Always reset loading state
      setIsChecking(false);
    }
  }, [isInitialized, getUserInfo]);

  /**
   * Gets the current user's JWT token for authenticated API requests
   * 
   * This is used to include the user's authentication token when sending
   * geolocation checks to the backend. This allows the backend to:
   * - Log which user performed the check
   * - Track compliance violations per user
   * - Enforce authentication requirements
   * 
   * @returns JWT token string or null if user is not authenticated
   */
  const getAuthToken = async (): Promise<string | null> => {
    try {
      // Get token from localStorage (set by AuthContext)
      const token = storage.getItem('auth_token');
      return token;
    } catch {
      return null;
    }
  };

  // Return hook interface
  return {
    checkGeolocation, // Function to perform geolocation check
    isChecking, // Whether a check is currently in progress
    isInitialized, // Whether SDK is ready to use
    lastCheckResult, // Result of the most recent check
    error, // Any error that occurred
  };
}

/**
 * Hook for automatic periodic geolocation checks
 * 
 * NOTE: This hook is now DEPRECATED in favor of handling periodic checks
 * directly in GeoComplyProvider. The provider now handles modal display
 * and logout logic when checks fail.
 * 
 * This hook is kept for backward compatibility but periodic check handling
 * should be done in GeoComplyProvider component.
 * 
 * @deprecated Use GeoComplyProvider's built-in periodic check handling instead
 * @param intervalMinutes - Number of minutes between checks (default: 20)
 */
export function useGeoComplyPeriodicCheck(intervalMinutes: number = 20) {
  const { checkGeolocation, isInitialized } = useGeoComply();
  const intervalMs = intervalMinutes * 60 * 1000; // Convert minutes to milliseconds

  useEffect(() => {
    // Don't start periodic checks if SDK isn't initialized
    if (!isInitialized) return;

    // Perform initial check immediately when component mounts
    // (This ensures we check location right away, not just after first interval)
    checkGeolocation();

    // Set up periodic checks at specified interval
    // This runs every 20 minutes (or whatever intervalMinutes is set to)
    const interval = setInterval(() => {
      checkGeolocation();
    }, intervalMs);

    // Cleanup: Clear interval when component unmounts or dependencies change
    return () => {
      clearInterval(interval);
    };
  }, [isInitialized, checkGeolocation, intervalMs]);
}

